import { useState, useCallback, useMemo } from 'react';
import type { DoctorShift } from '../../../../types/doctorShift';

/**
 * フィルター条件
 */
export interface ShiftFilterCondition {
  /** 医師名/医師ID検索キーワード */
  doctorKeyword: string;
  /** 時間帯開始 (HH:mm) */
  timeFrom: string;
  /** 時間帯終了 (HH:mm) */
  timeTo: string;
}

/**
 * useShiftFilter フックの戻り値
 */
export interface UseShiftFilterReturn {
  /** 現在のフィルター条件 */
  filter: ShiftFilterCondition;
  /** フィルターが適用中かどうか */
  isFiltering: boolean;
  /** 医師キーワードを設定 */
  setDoctorKeyword: (keyword: string) => void;
  /** 時間帯開始を設定 */
  setTimeFrom: (time: string) => void;
  /** 時間帯終了を設定 */
  setTimeTo: (time: string) => void;
  /** フィルター条件を一括設定 */
  setFilter: (filter: Partial<ShiftFilterCondition>) => void;
  /** フィルターをリセット */
  resetFilter: () => void;
  /** シフトデータをフィルタリング */
  filterShifts: (shifts: DoctorShift[]) => DoctorShift[];
}

/**
 * 初期フィルター条件
 */
const INITIAL_FILTER: ShiftFilterCondition = {
  doctorKeyword: '',
  timeFrom: '',
  timeTo: '',
};

/**
 * 時間文字列を分数に変換
 */
const timeToMinutes = (time: string): number => {
  const [hour, min] = time.split(':').map(Number);
  return hour * 60 + min;
};

/**
 * シフトフィルターフック
 *
 * @description
 * フィルター状態管理とシフト絞り込みロジックを提供する。
 * 医師名/ID、時間帯による絞り込みをサポート。
 *
 * @returns {UseShiftFilterReturn} フィルター管理関数群
 *
 * @example
 * const { filter, setDoctorKeyword, filterShifts } = useShiftFilter();
 * setDoctorKeyword('田中');
 * const filteredShifts = filterShifts(allShifts);
 */
export const useShiftFilter = (): UseShiftFilterReturn => {
  const [filter, setFilterState] = useState<ShiftFilterCondition>(INITIAL_FILTER);

  /**
   * フィルターが適用中かどうか
   */
  const isFiltering = useMemo(
    () =>
      filter.doctorKeyword !== '' ||
      filter.timeFrom !== '' ||
      filter.timeTo !== '',
    [filter]
  );

  /**
   * 医師キーワードを設定
   */
  const setDoctorKeyword = useCallback((keyword: string) => {
    setFilterState((prev) => ({ ...prev, doctorKeyword: keyword }));
  }, []);

  /**
   * 時間帯開始を設定
   */
  const setTimeFrom = useCallback((time: string) => {
    setFilterState((prev) => ({ ...prev, timeFrom: time }));
  }, []);

  /**
   * 時間帯終了を設定
   */
  const setTimeTo = useCallback((time: string) => {
    setFilterState((prev) => ({ ...prev, timeTo: time }));
  }, []);

  /**
   * フィルター条件を一括設定
   */
  const setFilter = useCallback((newFilter: Partial<ShiftFilterCondition>) => {
    setFilterState((prev) => ({ ...prev, ...newFilter }));
  }, []);

  /**
   * フィルターをリセット
   */
  const resetFilter = useCallback(() => {
    setFilterState(INITIAL_FILTER);
  }, []);

  /**
   * シフトデータをフィルタリング
   */
  const filterShifts = useCallback(
    (shifts: DoctorShift[]): DoctorShift[] => {
      if (!isFiltering) {
        return shifts;
      }

      return shifts.filter((shift) => {
        // 医師名/IDフィルター
        if (filter.doctorKeyword) {
          const keyword = filter.doctorKeyword.toLowerCase();
          const doctorName = shift.doctor_full_name?.toLowerCase() || '';
          const doctorId = shift.doctor_id.toLowerCase();
          const firstName = shift.doctor_first_name?.toLowerCase() || '';
          const lastName = shift.doctor_last_name?.toLowerCase() || '';

          if (
            !doctorName.includes(keyword) &&
            !doctorId.includes(keyword) &&
            !firstName.includes(keyword) &&
            !lastName.includes(keyword)
          ) {
            return false;
          }
        }

        // 時間帯フィルター
        if (filter.timeFrom || filter.timeTo) {
          const shiftStartMinutes = timeToMinutes(shift.start_time);
          const shiftEndMinutes = timeToMinutes(shift.end_time);

          if (filter.timeFrom) {
            const filterFromMinutes = timeToMinutes(filter.timeFrom);
            // シフトの終了時間がフィルター開始時間より前なら除外
            if (shiftEndMinutes <= filterFromMinutes) {
              return false;
            }
          }

          if (filter.timeTo) {
            const filterToMinutes = timeToMinutes(filter.timeTo);
            // シフトの開始時間がフィルター終了時間より後なら除外
            if (shiftStartMinutes >= filterToMinutes) {
              return false;
            }
          }
        }

        return true;
      });
    },
    [filter, isFiltering]
  );

  return {
    filter,
    isFiltering,
    setDoctorKeyword,
    setTimeFrom,
    setTimeTo,
    setFilter,
    resetFilter,
    filterShifts,
  };
};
